/**
 * PlatformNotificationsTab.jsx
 * 
 * Platform Admin tab for managing notification channels.
 * Supports Slack, Discord, Teams, generic Webhooks, and Email.
 * 
 * Place in: /src/components/PlatformNotificationsTab.jsx
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Bell, Plus, Trash2, Edit3, CheckCircle, XCircle, Loader2,
  RefreshCw, Send, ToggleLeft, ToggleRight, X, AlertTriangle,
  ExternalLink, Clock
} from 'lucide-react';
import { toast } from 'react-toastify';
import WatchdogStatusPanel from './WatchdogStatusPanel';

const CHANNEL_TYPES = {
  SLACK: { label: 'Slack', color: '#4A154B', placeholder: 'https://hooks.slack.com/services/T.../B.../xxx' },
  DISCORD: { label: 'Discord', color: '#5865F2', placeholder: 'https://discord.com/api/webhooks/...' },
  TEAMS: { label: 'Microsoft Teams', color: '#6264A7', placeholder: 'https://outlook.office.com/webhook/...' },
  WEBHOOK: { label: 'Custom Webhook', color: '#00d4aa', placeholder: 'https://your-server.com/webhook' },
  EMAIL: { label: 'Email', color: '#ea4335', placeholder: 'alerts@yourcompany.com' },
};

const formatDate = (d) => {
  if (!d) return 'Never';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const PlatformNotificationsTab = () => {
  const [channels, setChannels] = useState([]);
  const [eventTypes, setEventTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editChannel, setEditChannel] = useState(null);
  const [testing, setTesting] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '',
    type: 'SLACK',
    url: '',
    events: [],
    enabled: true,
  });

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/platform/notifications/channels', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setChannels(data.channels || []);
        setEventTypes(data.eventTypes || {});
      }
    } catch (err) {
      toast.error('Failed to load notification channels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChannels(); }, [fetchChannels]);

  const openCreateModal = () => {
    setEditChannel(null);
    setForm({ name: '', type: 'SLACK', url: '', events: [], enabled: true });
    setShowModal(true);
  };

  const openEditModal = (channel) => {
    setEditChannel(channel);
    const config = typeof channel.config === 'string' ? JSON.parse(channel.config) : channel.config;
    setForm({
      name: channel.name,
      type: channel.type,
      url: config.url || config.email || '',
      events: channel.events || [],
      enabled: channel.enabled,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      toast.error('Name and URL/Email are required');
      return;
    }
    setSaving(true);
    try {
      const config = form.type === 'EMAIL' ? { email: form.url } : { url: form.url };
      const body = {
        name: form.name.trim(),
        type: form.type,
        config,
        events: form.events,
        enabled: form.enabled,
      };

      const url = editChannel
        ? `/api/platform/notifications/channels/${editChannel.id}`
        : '/api/platform/notifications/channels';

      const res = await fetch(url, {
        method: editChannel ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.ok) {
        toast.success(editChannel ? 'Channel updated' : 'Channel created');
        setShowModal(false);
        fetchChannels();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save channel');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this notification channel?')) return;
    try {
      const res = await fetch(`/api/platform/notifications/channels/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        toast.success('Channel deleted');
        fetchChannels();
      }
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await fetch(`/api/platform/notifications/channels/${id}/toggle`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        fetchChannels();
      }
    } catch {
      toast.error('Failed to toggle channel');
    }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const res = await fetch(`/api/platform/notifications/channels/${id}/test`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Test notification sent');
      } else {
        toast.error('Test failed: ' + (data.error || 'Unknown error'));
      }
      fetchChannels();
    } catch {
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };

  const toggleEvent = (eventKey) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(eventKey)
        ? f.events.filter(e => e !== eventKey)
        : [...f.events, eventKey],
    }));
  };

  const selectAllEvents = () => {
    setForm(f => ({ ...f, events: Object.keys(eventTypes) }));
  };

  const deselectAllEvents = () => {
    setForm(f => ({ ...f, events: [] }));
  };

  // Group event types by category
  const eventsByCategory = {};
  Object.entries(eventTypes).forEach(([key, val]) => {
    const cat = val.category || 'Other';
    if (!eventsByCategory[cat]) eventsByCategory[cat] = [];
    eventsByCategory[cat].push({ key, ...val });
  });

  return (
    <div className="pn-tab">
      {/* Header */}
      <div className="pn-header">
        <div>
          <p className="pn-header-desc">
            Configure where ServerliCCM sends platform alerts: bug reports, agent status changes, server health, and more.
          </p>
        </div>
        <div className="pn-header-actions">
          <button className="btn btn-secondary btn-sm" onClick={fetchChannels} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button className="btn btn-primary btn-sm" onClick={openCreateModal}>
            <Plus size={14} /> Add Channel
          </button>
        </div>
      </div>

      {/* Watchdog Status */}
      <WatchdogStatusPanel />

      {/* Channels List */}
      {loading ? (
        <div className="pn-loading">
          <Loader2 size={24} className="spin" />
          <span>Loading channels...</span>
        </div>
      ) : channels.length === 0 ? (
        <div className="pn-empty">
          <Bell size={36} />
          <h3>No notification channels</h3>
          <p>Add a Slack webhook, Discord, or custom webhook to start receiving platform alerts.</p>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={16} /> Add Channel
          </button>
        </div>
      ) : (
        <div className="pn-channel-list">
          {channels.map(ch => {
            const typeCfg = CHANNEL_TYPES[ch.type] || CHANNEL_TYPES.WEBHOOK;
            const config = typeof ch.config === 'string' ? JSON.parse(ch.config) : ch.config;
            const url = config.url || config.email || '';
            const maskedUrl = url.length > 50 ? url.substring(0, 35) + '...' + url.substring(url.length - 12) : url;

            return (
              <div key={ch.id} className={`pn-channel-card ${!ch.enabled ? 'disabled' : ''}`}>
                <div className="pn-channel-header">
                  <div className="pn-channel-left">
                    <span className="pn-channel-type-badge" style={{ background: typeCfg.color + '20', color: typeCfg.color, borderColor: typeCfg.color + '40' }}>
                      {typeCfg.label}
                    </span>
                    <div className="pn-channel-info">
                      <span className="pn-channel-name">{ch.name}</span>
                      <code className="pn-channel-url">{maskedUrl}</code>
                    </div>
                  </div>
                  <div className="pn-channel-actions">
                    <button
                      className="pn-action-btn"
                      onClick={() => handleToggle(ch.id)}
                      title={ch.enabled ? 'Disable' : 'Enable'}
                    >
                      {ch.enabled
                        ? <ToggleRight size={20} color="#00d4aa" />
                        : <ToggleLeft size={20} color="#475569" />
                      }
                    </button>
                    <button
                      className="pn-action-btn"
                      onClick={() => handleTest(ch.id)}
                      disabled={testing === ch.id}
                      title="Send test notification"
                    >
                      {testing === ch.id ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                    </button>
                    <button className="pn-action-btn" onClick={() => openEditModal(ch)} title="Edit">
                      <Edit3 size={14} />
                    </button>
                    <button className="pn-action-btn danger" onClick={() => handleDelete(ch.id)} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="pn-channel-footer">
                  <div className="pn-channel-events">
                    {ch.events && ch.events.length > 0
                      ? ch.events.map(e => (
                          <span key={e} className="pn-event-tag">{eventTypes[e]?.label || e}</span>
                        ))
                      : <span className="pn-no-events">No events subscribed</span>
                    }
                  </div>
                  <div className="pn-channel-test-status">
                    {ch.lastTestAt && (
                      <span className={`pn-test-result ${ch.lastTestOk ? 'success' : 'failed'}`}>
                        {ch.lastTestOk ? <CheckCircle size={11} /> : <XCircle size={11} />}
                        {ch.lastTestOk ? 'Test passed' : 'Test failed'}
                        <span className="pn-test-time">{formatDate(ch.lastTestAt)}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content pn-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Bell size={18} /> {editChannel ? 'Edit Channel' : 'Add Notification Channel'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Channel Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Serverli Ops Slack, Bug Alerts"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>Type</label>
                <div className="pn-type-options">
                  {Object.entries(CHANNEL_TYPES).map(([key, cfg]) => (
                    <label key={key} className={`pn-type-option ${form.type === key ? 'selected' : ''}`}>
                      <input type="radio" name="channelType" value={key} checked={form.type === key} onChange={() => setForm(f => ({ ...f, type: key }))} />
                      <span className="pn-type-dot" style={{ background: cfg.color }} />
                      {cfg.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>{form.type === 'EMAIL' ? 'Email Address' : 'Webhook URL'}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={CHANNEL_TYPES[form.type]?.placeholder || 'https://...'}
                  value={form.url}
                  onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>
                  Events to Receive
                  <span className="pn-select-links">
                    <button type="button" onClick={selectAllEvents}>Select All</button>
                    <button type="button" onClick={deselectAllEvents}>Clear</button>
                  </span>
                </label>
                <div className="pn-events-grid">
                  {Object.entries(eventsByCategory).map(([cat, events]) => (
                    <div key={cat} className="pn-event-category">
                      <span className="pn-event-category-label">{cat}</span>
                      {events.map(evt => (
                        <label key={evt.key} className={`pn-event-checkbox ${form.events.includes(evt.key) ? 'checked' : ''}`}>
                          <input
                            type="checkbox"
                            checked={form.events.includes(evt.key)}
                            onChange={() => toggleEvent(evt.key)}
                          />
                          <div>
                            <strong>{evt.label}</strong>
                            <span>{evt.description}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><Loader2 size={14} className="spin" /> Saving...</> : (editChannel ? 'Save Changes' : 'Create Channel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformNotificationsTab;
